import { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useMatch } from 'react-router-dom';
import { useAuthContext } from '@/context/AuthContext';
import { ROUTES, PERMISSIONS } from '@/constants';
import { filterNavItems } from '@/utils/navAccess';
import { cn } from '@/lib/utils';
import {
    APP_SIDEBAR_ASIDE_CLASS,
    APP_SIDEBAR_HEADER_ROW_CLASS,
    APP_SIDEBAR_TOGGLE_BUTTON_CLASS,
    appSidebarNavClassName,
    APP_SIDEBAR_SECTION_LABEL_CLASS,
    appSidebarNavLinkClassName,
} from '@/lib/sidebarNav';
import { leadershipService } from '@/features/leadership/services/leadershipService';
import { LayoutDashboard, Columns3, Users, ChevronsUp, BookOpen, FileDown, Network, MonitorCog } from 'lucide-react';
import { TripleChevronCloseIcon, TripleChevronOpenIcon } from '@/components/layout/PortalSidebarToggle';
import { useIsMobile } from '@/hooks/useIsMobile';
import { ExecutiveAppHeader } from '@/components/layout/ExecutiveAppHeader';
import { DashboardHeaderSlotProvider, DashboardHeaderSlot } from '@/context/DashboardHeaderSlotContext';
import { ChatProvider } from '@/features/chat/context/ChatContext';
import { AiAssistantProvider } from '@/features/aiAssistant/context/AiAssistantContext';
import { AiAssistantNavItem } from '@/features/aiAssistant/components/AiAssistantNavItem';
import { useOperationalDashboardAccess } from '@/features/operationalDashboard/context/useOperationalDashboardAccess';

export function TaskForceChairLayout() {
    const isTaskForceDashboardHome = Boolean(useMatch({ path: ROUTES.TASK_FORCE_CHAIR, end: true }));
    const { user } = useAuthContext();
    const [tfName, setTfName] = useState('');
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const isMobile = useIsMobile();

    useEffect(() => {
        if (user?.userId) {
            leadershipService.getMyTaskForce().then((res) => {
                setTfName(res.data?.name || '');
            });
        }
    }, [user?.userId]);

    const { hasAccess: hasOperationalDashboard } = useOperationalDashboardAccess();

    const tabs = useMemo(
        () => [
            {
                label: 'Dashboard',
                to: ROUTES.TASK_FORCE_CHAIR,
                end: true,
                icon: LayoutDashboard,
                requiredPermissions: [PERMISSIONS.VIEW_TASK_FORCE],
            },
            {
                label: 'Pillar Management',
                to: ROUTES.TASK_FORCE_CHAIR_PILLARS,
                icon: Columns3,
                requiredPermissions: [PERMISSIONS.VIEW_PILLAR],
            },
            {
                label: 'Escalation History',
                to: ROUTES.TASK_FORCE_CHAIR_ESCALATION_HISTORY,
                icon: ChevronsUp,
                requiredPermissions: [PERMISSIONS.VIEW_TASK_FORCE],
            },
            {
                label: 'Structure',
                to: ROUTES.TASK_FORCE_CHAIR_STRUCTURE,
                icon: Network,
                // No permission or role gate: every account may see the structure.
                // Where the tree is rooted per role is decided by the server.
            },
            {
                label: 'Export',
                to: ROUTES.TASK_FORCE_CHAIR_EXPORT,
                icon: FileDown,
                // No permission or role gate: every account may export. What differs
                // per role is the scope, which the server derives from the account.
            },
            // Access is a per-account grant an admin hands out, not a
            // permission bit, so filterNavItems cannot gate it — the entry is
            // simply absent until the grant resolves.
            ...(hasOperationalDashboard
                ? [
                      {
                          label: 'Operational Dashboard',
                          to: ROUTES.TASK_FORCE_CHAIR_OPERATIONAL_DASHBOARD,
                          icon: MonitorCog,
                      },
                  ]
                : []),
            {
                label: 'User Manual',
                to: ROUTES.TASK_FORCE_CHAIR_HELP,
                icon: BookOpen,
                requiredPermissions: [PERMISSIONS.VIEW_TASK_FORCE],
            },
        ],
        [hasOperationalDashboard],
    );

    const visibleTabs = useMemo(() => filterNavItems(user, tabs), [user, tabs]);

    return (
        <DashboardHeaderSlotProvider>
            <ChatProvider>
                <AiAssistantProvider>
            <div className="flex h-screen h-dvh flex-col gap-3 overflow-hidden bg-background p-3">
                <ExecutiveAppHeader
                    title="Task Force Dashboard"
                    subtitle={tfName || 'Command Center'}
                    center={<DashboardHeaderSlot />}
                    showCountdown
                    onOpenSidebar={() => setSidebarOpen(true)}
                />

                <div className="relative flex min-h-0 flex-1 overflow-hidden">
                    {isMobile && sidebarOpen ? (
                        <div
                            className="fixed inset-0 z-30 bg-black/40 md:hidden"
                            onClick={() => setSidebarOpen(false)}
                            aria-hidden
                        />
                    ) : null}
                    <aside
                        className={cn(
                            APP_SIDEBAR_ASIDE_CLASS,
                            isMobile || sidebarOpen ? 'w-64' : 'w-14',
                            isMobile && !sidebarOpen ? '-translate-x-full' : 'translate-x-0',
                        )}
                    >
                        <div className={APP_SIDEBAR_HEADER_ROW_CLASS}>
                            {sidebarOpen ? (
                                <div className="flex min-w-[16rem] w-full justify-end px-2 py-2">
                                    <button
                                        type="button"
                                        onClick={() => setSidebarOpen(false)}
                                        className={APP_SIDEBAR_TOGGLE_BUTTON_CLASS}
                                        aria-label="Collapse sidebar to icons only"
                                        title="Show icons only"
                                    >
                                        <TripleChevronCloseIcon />
                                    </button>
                                </div>
                            ) : (
                                <div className="flex w-full justify-center py-2">
                                    <button
                                        type="button"
                                        onClick={() => setSidebarOpen(true)}
                                        className={APP_SIDEBAR_TOGGLE_BUTTON_CLASS}
                                        aria-label="Expand sidebar"
                                        title="Expand sidebar"
                                    >
                                        <TripleChevronOpenIcon />
                                    </button>
                                </div>
                            )}
                        </div>
                        <nav className={appSidebarNavClassName(sidebarOpen)}>
                            {sidebarOpen ? (
                                <p className={APP_SIDEBAR_SECTION_LABEL_CLASS}>
                                    Navigation
                                </p>
                            ) : null}
                            {visibleTabs.map(({ requiredPermissions: _rp, requiredRole: _rr, ...tab }) => (
                                <NavLink
                                    key={tab.to}
                                    to={tab.to}
                                    end={tab.end}
                                    title={tab.label}
                                    onClick={() => isMobile && setSidebarOpen(false)}
                                    className={({ isActive }) =>
                                        appSidebarNavLinkClassName({ isActive, expanded: sidebarOpen })
                                    }
                                >
                                    <tab.icon className="h-5 w-5 flex-shrink-0" aria-hidden />
                                    <span className={cn('leading-tight', !sidebarOpen && 'sr-only')}>{tab.label}</span>
                                </NavLink>
                            ))}
                            <AiAssistantNavItem
                                expanded={sidebarOpen}
                                onActivate={() => isMobile && setSidebarOpen(false)}
                            />
                        </nav>
                    </aside>

                    <main
                        className={cn(
                            'min-h-0 flex-1 p-0.5 md:p-3',
                            isTaskForceDashboardHome && !isMobile ? 'overflow-hidden' : 'overflow-y-auto overflow-x-hidden',
                        )}
                    >
                        <Outlet />
                    </main>
                </div>
            </div>
                </AiAssistantProvider>
            </ChatProvider>
        </DashboardHeaderSlotProvider>
    );
}

export default TaskForceChairLayout;
